import type { ArchiveDocumentForm, ArchiveDocumentHints, ArchiveDocumentHintValue, ArchiveDocumentType } from '@app/interfaces/archive-documents'
import { newBALZACBook } from '@app/interfaces/balzac-books'
import { newLandlord } from '@app/interfaces/landlord'
import { newPlace } from '@app/interfaces/place'
import { newSupplier } from '@app/interfaces/supplier'
import type { ArchiveDocumentDetail, ArchiveDocumentDetailHints } from '@app/generated/api/model'

export function toArchiveDocumentForm (dto: ArchiveDocumentDetail): ArchiveDocumentForm {
  return {
    filename: dto.filename,
    uuid: dto.uuid,
    type: dto.type ? toArchiveDocumentType(dto.type) : null,
    state: {
      name: dto.state.name,
      label_en: dto.state.label_en,
      label_fr: dto.state.label_fr,
      severity: dto.state.severity,
    },
    workflow: dto.workflow,
    transitions: dto.transitions.map((transition) => ({
      name: transition.name,
      label_en: transition.label_en,
      label_fr: transition.label_fr,
    })),
    is_modifiable: dto.is_modifiable,
    hints: toHints(dto.hints),
    voucher_code: dto.voucher_code ?? undefined,
    country: dto.country
      ? {
        id: dto.country.id,
        code_iso: dto.country.code_iso,
        code_iso_2: dto.country.code_iso_2,
        label_en: dto.country.label_en,
        label_fr: dto.country.label_fr,
      }
      : null,
    place: dto.place ? toPlace(dto.place.id, dto.place.name, dto.place.code) : null,
    project_codes: copyStrings(dto.project_codes),
    activity: dto.activity,
    date: dto.date ?? undefined,
    balzac_book: dto.balzac_book ? toBALZACBook(dto.balzac_book) : undefined,
    supplier: dto.supplier ? toSupplier(dto.supplier) : undefined,
    landlord: dto.landlord
      ? newLandlord({
        id: dto.landlord.id,
        code: dto.landlord.code ?? undefined,
        full_name: dto.landlord.full_name ?? undefined,
      })
      : undefined,
    partner: dto.partner ? toPartner(dto.partner.id, dto.partner.name) : undefined,
    third_party_code: dto.third_party_code ?? undefined,
    budget_lines: copyOptionalStrings(dto.budget_lines),
    asset_categories: copyOptionalStrings(dto.asset_categories),
    program_place: dto.program_place ? toPlace(dto.program_place.id, dto.program_place.name) : null,
    pmf_activity_number: dto.pmf_activity_number,
    asset_code: dto.asset_code,
    warehouse_code: dto.warehouse_code,
    document_number: dto.document_number,
    item_category: dto.item_category,
    order_form_date: dto.order_form_date,
    staff_name: dto.staff_name,
    homere_code: dto.homere_code,
  }
}

function toArchiveDocumentType (type: NonNullable<ArchiveDocumentDetail['type']>): ArchiveDocumentType {
  return {
    id: type.id,
    code: type.code,
    label_en: '',
    label_fr: '',
  }
}

function toPlace (id: number, name: string, code?: string | null) {
  const place = newPlace()
  place.id = id
  place.code = code ?? ''
  place.name = name
  return place
}

function toBALZACBook (source: NonNullable<ArchiveDocumentDetail['balzac_book']>) {
  const book = newBALZACBook()
  book.id = source.id
  book.partner = source.partner ? toPartner(source.partner.id, source.partner.name) : null
  book.saga_name = source.saga_name
  return book
}

function toSupplier (source: NonNullable<ArchiveDocumentDetail['supplier']>) {
  const supplier = newSupplier()
  supplier.id = source.id
  supplier.code = source.code
  supplier.name = source.name
  return supplier
}

function toPartner (id: number, name: string) {
  return { id, name }
}

function toHints (hints: ArchiveDocumentDetailHints | undefined): ArchiveDocumentHints {
  if (!isJsonRecord(hints)) {
    return {}
  }

  const clonedHints: ArchiveDocumentHints = {}
  for (const [fieldName, value] of Object.entries(hints)) {
    if (fieldName === 'type' && typeof value !== 'string') {
      continue
    }

    const clonedValue = cloneJsonValue(value)
    if (clonedValue !== undefined) {
      clonedHints[fieldName] = clonedValue
    }
  }

  return clonedHints
}

function cloneJsonValue (value: unknown): ArchiveDocumentHintValue | undefined {
  if (value === null || typeof value === 'string' || typeof value === 'boolean') {
    return value
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : undefined
  }

  if (Array.isArray(value)) {
    const clonedValues: ArchiveDocumentHintValue[] = []
    for (const item of value) {
      const clonedItem = cloneJsonValue(item)
      if (clonedItem !== undefined) {
        clonedValues.push(clonedItem)
      }
    }

    return clonedValues
  }

  if (!isJsonRecord(value)) {
    return undefined
  }

  const clonedObject: { [fieldName: string]: ArchiveDocumentHintValue } = {}
  for (const [fieldName, nestedValue] of Object.entries(value)) {
    const clonedValue = cloneJsonValue(nestedValue)
    if (clonedValue !== undefined) {
      clonedObject[fieldName] = clonedValue
    }
  }

  return clonedObject
}

function isJsonRecord (value: unknown): value is Record<string, unknown> {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return false
  }

  const prototype = Object.getPrototypeOf(value)
  return prototype === Object.prototype || prototype === null
}

function copyStrings (values: readonly (string | null)[] | null | undefined): string[] | null | undefined {
  if (values === null || values === undefined) {
    return values
  }

  return values.filter((value): value is string => value !== null)
}

function copyOptionalStrings (values: readonly (string | null)[] | null | undefined): string[] | undefined {
  if (values === null || values === undefined) {
    return undefined
  }

  return values.filter((value): value is string => value !== null)
}
